<template>
  <DxList :data-source="folders"
          :search-enabled="true"
          search-expr="folder_name"
          display-expr="display_text"
          :height="400"
          @item-click="navigate"
  />
</template>

<script setup>
  import { ref, onMounted } from 'vue'
  import { storeToRefs } from 'pinia'
  import _ from 'lodash'
  import DxList from 'devextreme-vue/list'
  import { useSearchPageStore } from '@/stores/searchPageStore.js'

  const searchPageStore = useSearchPageStore()
  const { headerFilter } = storeToRefs(searchPageStore)

  const folders = ref([])
  const pathSegments = ref([])

  const currentPath = () => pathSegments.value.join('/')

  const navigate = async (e) => {
    const item = e.itemData

    if (item.isBack)
      pathSegments.value.pop()
    else
      pathSegments.value.push(item.folder_name)

    // Generating path to use for filtering by doc_title
    const pathForFilter = currentPath()
    updateHeaderFilter(pathForFilter ? `${pathForFilter}/` : null)

    await loadFolders()
  }

  const loadFolders = async () => {
    const response = await searchPageStore.fetchFoldersByPath(currentPath())

    const backOption = pathSegments.value.length > 0
      ? [{ display_text: `← Exit "${pathSegments.value.at(-1)}"`, isBack: true }]
      : []

    const folderItems = response.map(item => ({
      ...item,
      display_text: `${item.folder_name} (${item.file_count})`
    }))

    folders.value = [...backOption, ...folderItems]
  }

  // We should create a js-module to store the complex logic to alter header filters
  const updateHeaderFilter = (path) => {
    const column = 'doc_title'
    let filter = _.cloneDeep(headerFilter.value)

    const isSimpleFilter = (f) => Array.isArray(f) && typeof f[0] === 'string'
    const isColumnFilter = (f) => Array.isArray(f) && f[0] === column

    // Remove doc_title filter when path is empty (root)
    if (!path) {
      if (_.isEmpty(filter)) {
        return
      }

      if (isSimpleFilter(filter)) {
        headerFilter.value = filter[0] === column ? null : filter
        return
      }

      // Complex filter - remove the column filter and clean up 'and' connectors
      const index = filter.findIndex(isColumnFilter)
      if (index !== -1) {
        filter.splice(index, 1)

        // Remove adjacent 'and' connector
        if (filter[index] === 'and') {
          filter.splice(index, 1)
        } else if (filter[index - 1] === 'and') {
          filter.splice(index - 1, 1)
        }

        // Simplify: single filter or empty
        headerFilter.value = filter.length === 0 ? null : filter.length === 1 ? filter[0] : filter
      }
      return
    }

    // Add/update doc_title filter for the given path
    const newFilter = [column, 'startswith', path]

    if (_.isEmpty(filter)) {
      headerFilter.value = newFilter
    } else if (isSimpleFilter(filter)) {
      headerFilter.value = filter[0] === column ? newFilter : [filter, 'and', newFilter]
    } else {
      const index = filter.findIndex(isColumnFilter)
      if (index !== -1) {
        filter[index] = newFilter
      } else {
        filter.push('and', newFilter)
      }
      headerFilter.value = filter
    }
  }

  onMounted(loadFolders)
</script>
